import { deleteData, fetchData, postData } from './api';

/*
-------------------------------
REMINDERS API
Served by the Rails API (LeaderRemindersController and MyRemindersController).

Recipients are never sent to the client. The API resolves them (members of
the group who haven't completed the current check-in) when the reminder goes
out, and the audience endpoint reports only a count, withheld below
MIN_AUDIENCE so a small group's non-completers can't be picked out.
-------------------------------
*/

export type ReminderChannel = 'email' | 'in_app';
/** check_in asks people to check in: it goes by email only, to those with a
 *  check-in open, since the app already sends them to the assessment. general
 *  is the leader's own message: to the whole group, by email and/or in-app,
 *  and it needs their note. */
export type ReminderType = 'check_in' | 'general';
export type ReminderStatus = 'scheduled' | 'sent' | 'cancelled';

/** GET /api/reminder_templates. `subject` and `body` are plain text with
 *  {{merge_fields}} (MERGE_FIELDS); body paragraphs are separated by a blank line. */
export interface ReminderTemplate {
  id: number;
  name: string;
  reminder_type: ReminderType;
  subject: string;
  body: string;
}

/** GET /api/reminder_audience?group_id=&reminder_type= */
export interface ReminderAudience {
  /** Null when suppressed. */
  count: number | null;
  /** True when fewer than MIN_AUDIENCE people would receive it. */
  suppressed: boolean;
}

/** POST /api/reminders */
export interface NewReminder {
  group_id: number;
  template_id: number;
  /** Plain text; the API escapes it. At most MAX_NOTE_LENGTH characters. */
  custom_note: string;
  channels: ReminderChannel[];
  /** ISO-8601; null sends now. */
  send_at: string | null;
}

/** An entry of GET /api/reminders?group_id=, and the POST's answer. */
export interface Reminder {
  id: number;
  group_id: number;
  template_name: string;
  sent_by: string;
  channels: ReminderChannel[];
  status: ReminderStatus;
  /** When it went, or will go, out. */
  send_at: string;
  /** Recipients once sent; null while scheduled or when fewer than MIN_AUDIENCE. */
  recipient_count: number | null;
}

/** GET /api/my_reminders: the viewer's undismissed in-app reminders, newest
 *  first. Only general ones: a check-in reminder never goes in-app. */
export interface MyReminder {
  id: number;
  subject: string;
  /** Plain text, merge fields already filled in. */
  body: string;
  from_name: string;
  created_at: string;
}

export const MIN_AUDIENCE = 3;
export const MAX_NOTE_LENGTH = 500;
/** The API sends at most one reminder of each type per group in this many days. */
export const RATE_LIMIT_DAYS = 7;

export const MERGE_FIELDS = ['leader_name', 'group_name', 'custom_note'] as const;
export type MergeFields = Record<(typeof MERGE_FIELDS)[number], string>;

export const getReminderTemplates = (): Promise<ReminderTemplate[]> =>
  fetchData({ endpoint: 'reminder_templates' }) as Promise<ReminderTemplate[]>;

export const getReminderAudience = (groupId: number, reminderType: ReminderType): Promise<ReminderAudience> =>
  fetchData({ endpoint: 'reminder_audience', filters: { group_id: groupId, reminder_type: reminderType } }) as Promise<ReminderAudience>;

export const getReminders = (groupId: number): Promise<Reminder[]> =>
  fetchData({ endpoint: 'reminders', filters: { group_id: groupId } }) as Promise<Reminder[]>;

export const createReminder = (reminder: NewReminder): Promise<Reminder> =>
  postData({ endpoint: 'reminders', payload: reminder }) as Promise<Reminder>;

export const cancelReminder = (id: number): Promise<Reminder> =>
  deleteData({ endpoint: `reminders/${id}` }) as Promise<Reminder>;

export const getMyReminders = (): Promise<MyReminder[]> =>
  fetchData({ endpoint: 'my_reminders' }) as Promise<MyReminder[]>;

export const dismissMyReminder = (id: number): Promise<unknown> =>
  postData({ endpoint: `my_reminders/${id}/dismiss` });

/**
 * The message a failed call should show: the API's own `error` (a 429 rate
 * limit or a 422 validation message, written for the user) when it sent one,
 * otherwise `fallback`.
 */
export const reminderErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof apiError === 'string' && apiError ? apiError : fallback;
};

/** Fills a template's {{merge_fields}}. An empty custom note drops its
 *  paragraph rather than leaving a blank one. Unknown fields are left as is. */
export const fillTemplate = (text: string, fields: MergeFields): string =>
  text
    .split(/\n{2,}/)
    .filter((paragraph) => !(paragraph.trim() === '{{custom_note}}' && !fields.custom_note.trim()))
    .join('\n\n')
    .replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
      (MERGE_FIELDS as readonly string[]).includes(name) ? fields[name as keyof MergeFields].trim() : match);
